import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { useT } from "../i18n/I18nProvider";
import type { AccountUser } from "../lib/auth";
import { ProfileSection } from "./sections/ProfileSection";
import { SecuritySection } from "./sections/SecuritySection";
import { PreferencesSection } from "./sections/PreferencesSection";
import { NotificationsSection } from "./sections/notifications";
import { PrivacySection } from "./sections/PrivacySection";
import { PERSONAL_SCREENS, personalSectionId } from "./shell/nav";
import { PageHead } from "./ui";
import type { TabKey } from "./tabs";

/**
 * Strip the frame off every kit `Card` inside the page: no border, no fill, no
 * shadow, no side padding. The screens are shared with the hub, which keeps
 * its cards; here a card title is just a sub-heading in one running page.
 */
const FLAT_CARDS = [
  "[&_[data-slot=card]]:rounded-none [&_[data-slot=card]]:border-0",
  "[&_[data-slot=card]]:bg-transparent [&_[data-slot=card]]:shadow-none",
  "[&_[data-slot=card]>div]:px-0 [&_[data-slot=card]>div:first-child]:pt-0",
].join(" ");

/** How long a deep link keeps its screen at the top while the ones above load. */
const PIN_MS = 2500;

/**
 * The portal's account page: all of the person's own screens on ONE page,
 * one after another, the way a music app lays out its settings.
 *
 * A client changes these a few times a year. Five separate pages, each a card
 * or two in a wide frame, made them hunt through the avatar menu for the next
 * one; one page reads top to bottom, and the side list (`shell/PersonalNav`)
 * shows where in it they are. The URL still names a screen (`?tab=security`),
 * and opening it scrolls that screen to the top.
 *
 * Only the portal draws this. The hub keeps one screen per tab beside its own
 * side menu.
 */
export function PersonalPage({
  active,
  user,
  onUserChange,
  onLogout,
}: {
  active: TabKey;
  user: AccountUser;
  onUserChange: (u: AccountUser) => void;
  onLogout: () => void;
}) {
  const t = useT();
  const pageRef = useRef<HTMLDivElement>(null);
  useJumpTo(active, pageRef);

  const screens: Partial<Record<TabKey, ReactNode>> = {
    profile: <ProfileSection user={user} onUserChange={onUserChange} />,
    preferences: <PreferencesSection />,
    security: <SecuritySection user={user} />,
    notifications: <NotificationsSection />,
    privacy: <PrivacySection user={user} onLogout={onLogout} />,
  };

  return (
    <div ref={pageRef} className={FLAT_CARDS}>
      <PageHead title={t("app.title")} />
      {PERSONAL_SCREENS.map(({ key }) => {
        const id = personalSectionId(key);
        return (
          <section
            key={key}
            id={id}
            aria-labelledby={`${id}-title`}
            className="scroll-mt-32 border-t border-border py-10 first-of-type:border-t-0 first-of-type:pt-2 lg:scroll-mt-24"
          >
            <h2 id={`${id}-title`} className="mb-5 text-xl font-bold tracking-tight">
              {t(`tabs.${key}`)}
            </h2>
            {screens[key]}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Scroll the named screen to the top whenever the URL names another one.
 *
 * The first screen goes to the very top, so the page heading shows with it.
 * The first jump is instant (the reader arrived from a link or the avatar
 * menu); later ones glide. A deep link lands before the screens above it have
 * loaded, and they grow as they do, so for a moment the target is held at the
 * top - until the reader moves the page themselves.
 */
function useJumpTo(active: TabKey, pageRef: RefObject<HTMLDivElement | null>) {
  const last = useRef<TabKey | null>(null);

  useEffect(() => {
    const el = document.getElementById(personalSectionId(active));
    if (!el) return;
    // A run for the screen the page is already on is the arrival again (React
    // runs effects twice in development), not a move: it lands and pins too.
    const first = last.current === null || last.current === active;
    last.current = active;
    const still = first || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior: ScrollBehavior = still ? "auto" : "smooth";
    if (active === PERSONAL_SCREENS[0]!.key) {
      window.scrollTo({ top: 0, behavior });
      return;
    }
    el.scrollIntoView({ block: "start", behavior });
    if (!first || !pageRef.current) return;

    let released = false;
    const release = () => {
      released = true;
    };
    const events = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
    events.forEach((name) => window.addEventListener(name, release, { passive: true }));
    const pin = new ResizeObserver(() => {
      if (!released) el.scrollIntoView({ block: "start" });
    });
    pin.observe(pageRef.current);
    const stop = window.setTimeout(() => pin.disconnect(), PIN_MS);
    return () => {
      window.clearTimeout(stop);
      pin.disconnect();
      events.forEach((name) => window.removeEventListener(name, release));
    };
  }, [active, pageRef]);
}
